import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays, Sun, Snowflake, Leaf, Flower2, TrendingUp, TrendingDown } from 'lucide-react';
import { useProductBundles } from '@/hooks/useProductBundles';
import { useBundleUsageStats } from '@/hooks/useBundleAnalytics';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format, subMonths, getMonth, getQuarter } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

const SEASONS = [
  { name: 'Spring', months: [2, 3, 4], icon: Flower2, color: 'text-green-500' },
  { name: 'Summer', months: [5, 6, 7], icon: Sun, color: 'text-yellow-500' },
  { name: 'Fall', months: [8, 9, 10], icon: Leaf, color: 'text-orange-500' },
  { name: 'Winter', months: [11, 0, 1], icon: Snowflake, color: 'text-blue-500' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function useSeasonalityData() {
  return useQuery({
    queryKey: ['bundle-seasonality'],
    queryFn: async () => {
      const { data: lineItems, error } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          total_price,
          created_at,
          deal:deals(status)
        `)
        .not('bundle_id', 'is', null)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const { data: bundles } = await supabase
        .from('product_bundles')
        .select('id, name');

      const bundleMap = new Map(bundles?.map(b => [b.id, b.name]) || []);

      // Group by bundle and month
      const monthlyData: Record<string, Record<number, { revenue: number; deals: number; wonDeals: number }>> = {};
      
      lineItems?.forEach((item: any) => {
        if (!item.bundle_id) return;
        
        const month = getMonth(new Date(item.created_at));
        const bundleId = item.bundle_id;
        
        if (!monthlyData[bundleId]) {
          monthlyData[bundleId] = {};
          for (let i = 0; i < 12; i++) {
            monthlyData[bundleId][i] = { revenue: 0, deals: 0, wonDeals: 0 };
          }
        }
        
        monthlyData[bundleId][month].revenue += item.total_price;
        monthlyData[bundleId][month].deals += 1;
        if (item.deal?.status === 'won') {
          monthlyData[bundleId][month].wonDeals += 1;
        }
      });

      // Calculate seasonal performance for each bundle
      const bundleSeasonality = Object.entries(monthlyData).map(([bundleId, months]) => {
        const seasonalPerformance = SEASONS.map(season => {
          const seasonData = season.months.reduce((acc, month) => ({
            revenue: acc.revenue + months[month].revenue,
            deals: acc.deals + months[month].deals,
            wonDeals: acc.wonDeals + months[month].wonDeals,
          }), { revenue: 0, deals: 0, wonDeals: 0 });
          
          return {
            season: season.name,
            ...seasonData,
            avgRevenue: seasonData.deals > 0 ? seasonData.revenue / seasonData.deals : 0,
          };
        });

        const bestSeason = seasonalPerformance.reduce((best, current) => 
          current.revenue > best.revenue ? current : best
        );

        const monthlyPerformance = Object.entries(months).map(([month, data]) => ({
          month: MONTHS[parseInt(month)],
          revenue: data.revenue,
          deals: data.deals,
        }));

        return {
          bundleId,
          bundleName: bundleMap.get(bundleId) || 'Unknown',
          seasonalPerformance,
          monthlyPerformance,
          bestSeason: bestSeason.season,
          bestSeasonRevenue: bestSeason.revenue,
        };
      });

      return bundleSeasonality;
    },
  });
}

export function BundleSeasonalityAnalysis() {
  const { data: seasonalityData, isLoading } = useSeasonalityData();
  const [selectedBundle, setSelectedBundle] = useState<string>('all');

  const filteredData = useMemo(() => {
    if (!seasonalityData) return null;
    if (selectedBundle === 'all') return seasonalityData;
    return seasonalityData.filter(d => d.bundleId === selectedBundle);
  }, [seasonalityData, selectedBundle]);

  const aggregatedMonthlyData = useMemo(() => {
    if (!filteredData) return [];
    
    const aggregated: Record<string, number> = {};
    MONTHS.forEach(m => aggregated[m] = 0);
    
    filteredData.forEach(bundle => {
      bundle.monthlyPerformance.forEach(mp => {
        aggregated[mp.month] += mp.revenue;
      });
    });

    return MONTHS.map(month => ({
      month,
      revenue: aggregated[month],
    }));
  }, [filteredData]);

  const aggregatedSeasonalData = useMemo(() => {
    if (!filteredData) return [];
    
    const aggregated: Record<string, { revenue: number; deals: number }> = {};
    SEASONS.forEach(s => aggregated[s.name] = { revenue: 0, deals: 0 });
    
    filteredData.forEach(bundle => {
      bundle.seasonalPerformance.forEach(sp => {
        aggregated[sp.season].revenue += sp.revenue;
        aggregated[sp.season].deals += sp.deals;
      });
    });

    return SEASONS.map(season => ({
      season: season.name,
      revenue: aggregated[season.name].revenue,
      deals: aggregated[season.name].deals,
      Icon: season.icon,
      color: season.color,
    }));
  }, [filteredData]);

  const bestPerformingSeason = useMemo(() => {
    if (!aggregatedSeasonalData.length) return null;
    return aggregatedSeasonalData.reduce((best, current) => 
      current.revenue > best.revenue ? current : best
    );
  }, [aggregatedSeasonalData]);

  const seasonTrend = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const currentSeason = SEASONS.find(s => s.months.includes(currentMonth));
    const seasonData = aggregatedSeasonalData.find(s => s.season === currentSeason?.name);
    return { currentSeason, seasonData };
  }, [aggregatedSeasonalData]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading seasonality data...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            <CardTitle>Bundle Seasonality Analysis</CardTitle>
          </div>
          <Select value={selectedBundle} onValueChange={setSelectedBundle}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select bundle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bundles</SelectItem>
              {seasonalityData?.map(bundle => (
                <SelectItem key={bundle.bundleId} value={bundle.bundleId}>
                  {bundle.bundleName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <CardDescription>Identify which bundles perform better during specific time periods</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Season Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {aggregatedSeasonalData.map(season => {
            const Icon = season.Icon;
            const isBest = season.season === bestPerformingSeason?.season;
            const isCurrent = season.season === seasonTrend.currentSeason?.name;
            
            return (
              <Card key={season.season} className={`${isBest ? 'ring-2 ring-primary' : ''}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`h-5 w-5 ${season.color}`} />
                    <div className="flex gap-1">
                      {isBest && <Badge variant="default" className="text-xs">Best</Badge>}
                      {isCurrent && <Badge variant="outline" className="text-xs">Current</Badge>}
                    </div>
                  </div>
                  <p className="font-semibold">{season.season}</p>
                  <p className="text-2xl font-bold">${(season.revenue / 1000).toFixed(1)}K</p>
                  <p className="text-xs text-muted-foreground">{season.deals} deals</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Monthly Trend Chart */}
        <div className="space-y-2">
          <h4 className="font-semibold">Monthly Revenue Trend</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={aggregatedMonthlyData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                <Tooltip 
                  formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="hsl(var(--primary))" 
                  fillOpacity={1} 
                  fill="url(#colorRevenue)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bundle Season Rankings */}
        {selectedBundle === 'all' && seasonalityData && seasonalityData.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold">Bundle Best Seasons</h4>
            <div className="grid gap-2">
              {seasonalityData.map(bundle => {
                const season = SEASONS.find(s => s.name === bundle.bestSeason);
                const Icon = season?.icon || CalendarDays;
                
                return (
                  <div key={bundle.bundleId} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${season?.color || 'text-muted-foreground'}`} />
                      <span className="font-medium">{bundle.bundleName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{bundle.bestSeason}</Badge>
                      <span className="text-sm text-muted-foreground">
                        ${bundle.bestSeasonRevenue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(!seasonalityData || seasonalityData.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No seasonality data available yet</p>
            <p className="text-sm">Bundle usage will be analyzed over time</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
